import React from "react";
import BlocksContainerEditor from "./blocks/BlocksContainerEditor";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faPlus, faTrash, faTrashCan} from "@fortawesome/free-solid-svg-icons";
import Button from "../../../../../../components/elements/Button";
import InputField from "../../../../../../components/elements/InputField";
import CustomSelect from "../../../../../../components/elements/CustomSelect";
import {LANGUAGES} from "../../../../../../utils/constants";

import StepEditorsManager from "./step/StepEditorsManager";
import RunTargetsEditor from "./step/RunTargetsEditor";
import ValidationChecksEditor from "./step/ValidationChecksEditor";
import TextBlockEditor from "../lesson/TextBlockEditor";
import EditableField from "../../../../../../components/elements/EditableField";

export default function StepEditorCard({step, path, updateAtPath, onDelete, canDelete}) {

    // Helpers for deletion safety
    const referencedEditorIds = (step.run?.targets || []).flatMap(t =>
        t.resultType === 'web' ? Object.values(t.editorIds || {}) : [t.editorId]
    ).filter(Boolean);

    const referencedTargetIds = (step.validation?.checks || []).map(c => c.targetId).filter(Boolean);

    return (
        <div style={{border: "1px solid #eee", padding: 12, background: "#fff", display: "grid", gap: 16}}>
            {/* 1. Header & ID */}
            <div style={{display: "flex", justifyContent: "space-between"}}>
                <strong>Step: {step.id}</strong>
                <Button onClick={onDelete} disabled={!canDelete}>
                    <FontAwesomeIcon icon={faTrash} size={'lg'}/>
                </Button>
            </div>

            {/* 2. Instruction (Blocks) */}
            <BlocksContainerEditor
                title="Instruction"
                container={step.instruction}
                path={[...path, "instruction"]}
                updateAtPath={updateAtPath}
            />

            {/* 3. Editors Manager */}
            <StepEditorsManager
                editors={step.editors}
                path={[...path, "editors"]}
                updateAtPath={updateAtPath}
                referencedIds={referencedEditorIds}
            />

            {/* 4. Run Targets */}
            <RunTargetsEditor
                targets={step.run.targets}
                editors={step.editors}
                path={[...path, "run", "targets"]}
                updateAtPath={updateAtPath}
                referencedIds={referencedTargetIds}
            />

            {/* 5. Validation */}
            <ValidationChecksEditor
                validation={step.validation}
                targets={step.run.targets}
                path={[...path, "validation"]}
                updateAtPath={updateAtPath}
            />

            {/* 6. Feedback */}
            <div className='grid grid-cols-2 gap-8 my-10'>
                <div>
                    <label className='mb-3 block text-lg font-bold'>Correct Feedback</label>
                    <TextBlockEditor
                        block={step.feedback.correct}
                        onChange={(value) =>
                            updateAtPath([...path, "feedback", "correct"], value)
                        }
                    />
                </div>
                <div>
                    <label className='mb-3 block text-lg font-bold'>Incorrect Feedback</label>
                    <TextBlockEditor
                        block={step.feedback.incorrect}
                        onChange={(value) =>
                            updateAtPath([...path, "feedback", "incorrect"], value)
                        }
                    />
                </div>
            </div>

            {/* 7. Hints & Explain */}
            <HintsSection step={step} path={path} updateAtPath={updateAtPath}/>

            <BlocksContainerEditor
                title="Explain (Post-Success)"
                container={step.explain}
                path={[...path, "explain"]}
                updateAtPath={updateAtPath}
            />
        </div>
    );
}

function HintsSection({step, path, updateAtPath}) {
    const hints = step.hints || [];

    const addHint = () => {
        // lazy import to avoid circulars; or move factory out.
        const {createEmptyHint} = require("./factories");
        updateAtPath([...path, "hints"], hints.concat([createEmptyHint()]));
    };

    const deleteHint = (hintIndex) => {
        const next = hints.filter((_, i) => i !== hintIndex);
        updateAtPath([...path, "hints"], next);
    };

    return (
        <div className="flex flex-col gap-5 m-10">
            <div className='flex items-center gap-3 mb-5'>
                <h3>Hints</h3>
                <Button onClick={addHint} className='w-fit'>
                    <span>Add Hint</span>
                    <FontAwesomeIcon icon={faPlus} size={'lg'}/>
                </Button>
            </div>

            {hints.length === 0 ? (
                <span>No hints</span>
            ) : (
                hints.map((hint, hintIndex) => (
                    <div
                        key={hint.id || hintIndex}
                        style={{
                            border: "1px solid #eee",
                            borderRadius: 8,
                            padding: 10,
                            display: "grid",
                            gap: 8,
                        }}
                    >
                        <div style={{display: "flex", alignItems: "center", gap: 8}}>
                            <div style={{fontSize: 12, opacity: 0.7}}>Hint: {hint.id}</div>
                            <div style={{flex: 1}}/>
                            <button type="button" onClick={() => deleteHint(hintIndex)}>
                                <FontAwesomeIcon icon={faTrashCan} size='md' />
                            </button>
                        </div>

                        <div className='block'>
                            <label>Energy Cost</label>
                            <EditableField
                                type="number"
                                value={hint.energyCost ?? 0}
                                onChange={(value) =>
                                    updateAtPath([...path, "hints", hintIndex, "energyCost"], value)
                                }
                                onSave={(value) =>
                                    updateAtPath([...path, "hints", hintIndex, "energyCost"], Number(value))
                                }
                            />
                        </div>

                        {/* Hint is blocks[] directly per schema */}
                        <div style={{display: "grid", gap: 6}}>
                            <BlocksContainerEditor
                                title={null}
                                container={{blocks: hint.blocks || []}}
                                path={[...path, "hints", hintIndex]}
                                updateAtPath={(p, v) => {
                                    // We want to write to hint.blocks.
                                    // BlocksContainerEditor writes to {blocks}, so we map it.
                                    if (p[p.length - 1] === "blocks") {
                                        updateAtPath([...path, "hints", hintIndex, "blocks"], v);
                                    } else {
                                        updateAtPath(p, v);
                                    }
                                }}
                            />
                        </div>
                    </div>
                ))
            )}
        </div>
    );
}
